<template>
	<div class="relative flex flex-col">
		<div class="text-white bg-blue-darker">
			<div class="header-container relative">
				<ul class="flex justify-center items-center h-[42px] text-[15px]">
					<li v-for="(item, idx) in topBarItems" :key="idx" class="px-4">
						<img
							v-if="item.image?.[0]?.image?.filename"
							:src="item.image[0].image.filename"
							alt=""
							class="inline"
						/>
						<span class="ml-2">{{ item.text }}</span>
					</li>
				</ul>
			</div>
		</div>
		<div
			class="w-full h-full py-5 px-[50px] flex justify-between items-center bg-blue"
		>
			<div class="header-container w-full h-full flex justify-between">
				<div class="w-4/12 flex">
					<div
						class="grid grid-cols-[max-content_max-content] gap-2 w-full text-xs text-white gap-y-2.5 h-12 items-center"
					>
						<div class="inline-flex gap-1 text-xs items-end">
							<PhoneIcon class="h-4 w-4 text-white" />
							<nuxt-link
								:to="localePath(topNavbarLeft?.[0]?.Link?.cached_url || '/')"
								class="flex flex-col items-center"
							>
								<span class="text-sm leading-none">
									{{ topNavbarLeft?.[0]?.text }}
								</span>
							</nuxt-link>
						</div>
						<div class="inline-flex gap-1 text-xs items-end">
							<EnvelopeIcon class="h-4 w-4 text-white" />
							<nuxt-link
								:to="localePath(topNavbarLeft?.[1]?.Link?.cached_url || '/')"
								class="flex flex-col items-center"
							>
								<span class="text-sm leading-none">
									{{ topNavbarLeft?.[1]?.text }}
								</span>
							</nuxt-link>
						</div>
						<div class="inline-flex gap-1 text-xs items-end">
							<HomeIcon class="h-4 w-4 text-white" />
							<p class="flex flex-col items-center">
								<span class="text-sm leading-none">
									{{ topNavbarLeft?.[2]?.text }}
								</span>
							</p>
						</div>
					</div>
				</div>
				<div class="flex justify-center items-center w-4/12">
					<NuxtLink :to="localePath(`/`)">
						<NuxtImg
							:src="siteLogo?.filename"
							:alt="siteLogo?.alt || 'logo'"
							class="logo max-w-[200px]"
							loading="eager"
							:width="siteLogoDimensions?.width"
							:height="siteLogoDimensions?.height"
						/>
					</NuxtLink>
				</div>
				<div class="w-4/12 flex gap-4 justify-end items-center">
					<StoryblokComponent
						v-for="blok in search"
						:key="blok._uid"
						:blok="blok"
						class="max-w-[250px]"
					/>

					<div class="flex gap-4 cursor-pointer">
						<div class="relative cursor-pointer" @click="openWishlist">
							<HeartIcon class="h-6 w-6 text-white" />
							<client-only>
								<span
									v-if="wishlistProducts?.ItemCount"
									class="text-[10px] font-medium text-white text-center absolute bg-green-light rounded-full w-5 h-5 leading-5 -top-2 -right-2"
								>
									{{ wishlistProducts?.ItemCount }}
								</span>
							</client-only>
						</div>
						<div
							class="relative cursor-pointer"
							data-testid="cart-toggle"
							@click="isSidebarOpen = true"
						>
							<ShoppingBagIcon class="h-6 w-6 text-white" />
							<client-only>
								<span
									data-testid="cart-badge"
									class="text-[10px] font-medium text-white text-center absolute bg-green-light rounded-full w-5 h-5 leading-5 -top-2 -right-2"
								>
									{{ itemsCount || 0 }}
								</span>
							</client-only>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="h-full text-white bg-blue-dark">
			<div class="header-container h-full w-full flex flex-wrap justify-center">
				<!-- The Menu Node menu, or the old Norce categories until the space
					has one (GF-85). -->
				<LayoutCategoriesNavigation
					:header-mode="'default'"
					:menu="menuItems"
				/>
				<!-- Old Storyblok menu links as secondary items. The new menu holds
					its own links, so these only show while the old menu is in use. -->
				<template v-if="!usesNewMenu">
					<StoryblokComponent
						v-for="blok in menuCategories"
						:key="blok._uid"
						:blok="blok"
					/>
				</template>
			</div>
		</div>
		<LayoutSideCart />
	</div>
</template>

<script setup lang="ts">
import { HeartIcon, ShoppingBagIcon } from "@heroicons/vue/24/outline";
import { PhoneIcon, EnvelopeIcon, HomeIcon } from "@heroicons/vue/24/solid";
import { computed, inject, provide, ref } from "vue";
import { storeToRefs } from "pinia";
import type { Category } from "@nwt/norce-pwa/dist/runtime/types/api/category/category";
import type { Currency } from "@nwt/norce-pwa/dist/runtime/types/api/application/application";
import type { SharedModal } from "~/layers/base/components/Shared/SharedModal.vue";
import type {
	CulturesNorce,
	SiteLogoDimensions
} from "~/types/components/header";
import type {
	MenuLink,
	ResolvedMenuNode,
	Search,
	SiteLogo,
	TopNavbarLeft,
	TopBarItem
} from "~/types/storyblok/storyblok";

const cartStore = useCartStore();
const modal = inject<SharedModal>("modal") as SharedModal;

const itemsCount = computed(() => cartStore.itemsCount());
const { wishlistProducts } = storeToRefs(useWishlistStore());

const localePath = useLocalePath();
const isSidebarOpen = ref(false);

provide("isSidebarOpen", isSidebarOpen);

interface Props {
	topNavbarLeft: TopNavbarLeft[] | undefined;
	culturesNorce: CulturesNorce[] | undefined;
	currenciesNorce: Currency[] | undefined;
	search: Search[] | undefined;
	siteLogo: SiteLogo | undefined;
	siteLogoDimensions: SiteLogoDimensions;
	menuCategoriesNorce: Category[] | undefined;
	menuCategories: MenuLink[] | undefined;
	topBarItems: TopBarItem[] | undefined;
	menu?: ResolvedMenuNode[];
}

const {
	topNavbarLeft,
	search,
	siteLogo,
	siteLogoDimensions,
	menuCategoriesNorce,
	menuCategories,
	topBarItems,
	menu
} = defineProps<Props>();

const { items: menuItems, usesNewMenu } = useMenuWithFallback(() => ({
	menu,
	menuCategoriesNorce
}));

const openWishlist = async () => {
	modal.open("AccountWishlist", {
		position: "side"
	});
};
</script>
